import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Plus, Folder, CheckCircle, FileText, Sparkles, Languages, 
  Settings, LogOut, ChevronDown, Monitor, FileQuestion, BookOpen, Crown, Receipt
} from 'lucide-react';
import NewProjectModal from '../NewProjectModal';

const AppLayout = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNewModal, setShowNewModal] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileRef]);

  const openEditorProfileView = (view) => {
    setProfileOpen(false);
    navigate(`/editor?view=${view}`);
  };

  const isPremium = user?.role === 'admin' || (user?.plan && user.plan !== 'free');
  const initial = user?.name ? user.name.charAt(0).toUpperCase() : 'U';

  const SIDEBAR_TOOLS = [
    { id: 'grammar', label: 'Grammar Checker', icon: <CheckCircle size={22} /> },
    { id: 'tagline', label: 'Tagline Generator', icon: <Sparkles size={22} /> },
    { id: 'summarize', label: 'Summariser', icon: <FileText size={22} /> },
    { id: 'ai_rewrite', label: 'AI Writer', icon: <Sparkles size={22} /> },
    { id: 'translate', label: 'Translator', icon: <Languages size={22} /> },
  ];

  return (
    <div style={styles.layout}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.logoContainer} onClick={() => navigate('/')}>
            <div style={styles.logoIcon}>TG</div>
            <span style={styles.logoText}>TuraGrammar</span>
        </div>

        {user?.role !== 'admin' && <div style={styles.sidebarSection}>
            <div style={styles.newBtn} onClick={() => setShowNewModal(true)}>
                <Plus size={20} />
                <span>New</span>
            </div>
        </div>}

        {user?.role !== 'admin' && <div style={styles.sidebarSection}>
            <Link to="/projects" style={location.pathname === '/projects' ? styles.sidebarItemActive : styles.sidebarItem}>
                <Folder size={20} />
                <span>Projects</span>
            </Link>
        </div>}



        <div style={styles.divider}></div>

        {user?.role !== 'admin' && <div style={styles.sidebarSection}>
            {SIDEBAR_TOOLS.map(tool => {
                const isActive = location.pathname === '/editor' && location.search.includes(`type=${tool.id}`);
                const isGrammarActive = location.pathname === '/editor' && (location.search.includes(`type=grammar`) || location.search === '');
                const finalActive = tool.id === 'grammar' ? isGrammarActive : isActive;

                return (
                    <div 
                        key={tool.id} 
                        style={finalActive ? styles.sidebarItemActive : styles.sidebarItem}
                        onClick={() => navigate(`/editor?type=${tool.id}`)}
                    >
                        {tool.icon}
                        <span style={{textAlign: 'center', lineHeight: '1.2'}}>{tool.label}</span>
                    </div>
                )
            })}
        </div>}
        
        <div style={styles.divider}></div>
        
        {user?.role !== 'admin' && <div style={styles.sidebarSection}>
            <div style={styles.sidebarItem}>
                <Monitor size={20} />
                <span>Apps & Extensions</span>
                <ChevronDown size={14} style={{marginLeft: 'auto'}} />
            </div>
        </div>}

      </div>

      {/* Main Content Area */}
      <div style={styles.main}>
        {/* Top Header */}
        <header style={styles.header}>
            <div style={styles.headerLeft}></div>
            <div style={styles.headerRight}>
                {!isPremium && (
                    <Link to="/pricing" style={styles.premiumBtn}>
                        <Crown size={16} />
                        Upgrade to Premium
                    </Link>
                )}
                
                {/* Profile Dropdown */}
                <div style={{position: 'relative'}} ref={profileRef}>
                    <div style={styles.avatar} onClick={() => setProfileOpen(!profileOpen)}>
                        {initial}
                    </div>
                    {profileOpen && (
                        <div style={styles.dropdown}>
                            <div style={styles.dropdownHeader}>
                                <div style={styles.dropdownEmail}>{user?.email}</div>
                            </div>
                            <div style={styles.dropdownList}>
                                <div style={styles.dropdownItem} onClick={() => openEditorProfileView('account')}>
                                    <BookOpen size={16} /> Account details
                                </div>
                                <div style={styles.dropdownItem} onClick={() => openEditorProfileView('dark_mode')}>
                                    <Monitor size={16} /> Dark mode
                                    <div style={styles.toggleTrack}><div style={styles.toggleThumb}></div></div>
                                </div>
                                <div style={styles.dropdownItem} onClick={() => openEditorProfileView('settings')}>
                                    <Settings size={16} /> Settings
                                </div>
                                <Link to="/billing-history" style={styles.dropdownItem} onClick={() => setProfileOpen(false)}>
                                    <Receipt size={16} /> Billing history
                                </Link>
                                <div style={styles.dropdownItem} onClick={() => openEditorProfileView('legal')}>
                                    <FileQuestion size={16} /> Legal
                                </div>
                                {user?.role === 'admin' && (
                                    <Link to="/admin" style={styles.dropdownItem} onClick={() => setProfileOpen(false)}>
                                        <Settings size={16} /> Admin Panel
                                    </Link>
                                )}
                                <div style={styles.dropdownItem} onClick={() => openEditorProfileView('logout')}>
                                    <LogOut size={16} /> Log out
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>

        {/* Page Content */}
        <div style={styles.contentWrapper}>
            <div style={styles.floatingCard}>
                {children}
            </div>
        </div>
      </div>
      
      {showNewModal && <NewProjectModal onClose={() => setShowNewModal(false)} />}
    </div>
  );
};

const styles = {
  layout: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    background: 'linear-gradient(145deg, #e8f0fe 0%, #eef2ff 40%, #f0f4ff 100%)',
    fontFamily: '"Inter", sans-serif',
  },
  sidebar: {
    width: '132px',
    backgroundColor: 'transparent',
    borderRight: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 0',
    overflowY: 'auto',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 12px 24px 12px',
    cursor: 'pointer',
  },
  logoIcon: {
    width: '24px',
    height: '24px',
    backgroundColor: '#4f46e5',
    color: 'white',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '12px'
  },
  logoText: {
    fontSize: '18px',
    fontWeight: 800,
    color: '#0f172a',
  },
  newBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    padding: '10px 16px',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: '16px',
    width: 'max-content',
    margin: '0 auto 16px auto',
  },
  sidebarSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
  },
  bottomSidebarSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: 'auto',
  },
  sidebarItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '12px 4px',
    borderRadius: '12px',
    color: '#475569',
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 500,
    width: '112px',
    transition: 'background 0.2s, opacity 0.2s',
    opacity: 0.7,
  },
  sidebarItemActive: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '12px 4px',
    borderRadius: '12px',
    color: '#0f172a',
    backgroundColor: '#ffffff',
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 600,
    width: '112px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    opacity: 1,
  },
  divider: {
    display: 'none',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    height: '60px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  premiumBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    padding: '6px 14px',
    borderRadius: '16px',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 600,
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#8b5cf6',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    border: '2px solid #fff',
    boxShadow: '0 0 0 1px #e2e8f0',
  },
  dropdown: {
    position: 'absolute',
    top: '48px',
    right: '0',
    width: '240px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
    zIndex: 50,
  },
  dropdownHeader: {
    padding: '16px',
    borderBottom: '1px solid #e2e8f0',
  },
  dropdownEmail: {
    fontSize: '13px',
    color: '#64748b',
    wordBreak: 'break-all',
  },
  dropdownList: {
    padding: '8px 0',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    color: '#334155',
    textDecoration: 'none',
    fontSize: '14px',
    cursor: 'pointer',
  },
  toggleTrack: {
    marginLeft: 'auto',
    width: '32px',
    height: '18px',
    backgroundColor: '#cbd5e1',
    borderRadius: '10px',
    position: 'relative',
  },
  toggleThumb: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '14px',
    height: '14px',
    backgroundColor: 'white',
    borderRadius: '50%',
  },
  contentWrapper: {
    flex: 1,
    display: 'flex',
    padding: '0 24px 24px 24px',
    overflow: 'hidden',
  },
  floatingCard: {
    flex: 1,
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    position: 'relative',
  }
};

export default AppLayout;
